import { Token, TokenType, ASTNode } from './types';
import { Tokenizer } from './tokenizer';

export class Parser {
    private readonly tokens: Token[];
    private pos: number;

    constructor(tokens: Token[]) {
        this.tokens = tokens;
        this.pos = 0;
    }

    static parse(input: string): ASTNode {
        const tokenizer = new Tokenizer(input);
        const tokens = tokenizer.tokenize();
        const parser = new Parser(tokens);
        return parser.parseExpression();
    }

    private current(): Token {
        return this.tokens[this.pos] ?? { type: TokenType.EOF, value: '', position: -1 };
    }

    private peek(offset: number = 0): Token {
        return this.tokens[this.pos + offset] ?? { type: TokenType.EOF, value: '', position: -1 };
    }

    private advance(): Token {
        const token = this.current();
        this.pos++;
        return token;
    }

    private expect(type: TokenType): Token {
        const token = this.current();
        if (token.type !== type) {
            throw new Error(`Expected ${TokenType[type]} but got ${TokenType[token.type]} ("${token.value}") at position ${token.position}`);
        }
        return this.advance();
    }

    parseExpression(): ASTNode {
        return this.parseComparison();
    }

    private parseComparison(): ASTNode {
        let left = this.parseConcat();

        while (this.isComparisonOp(this.current().type)) {
            const op = this.advance().value;
            const right = this.parseConcat();
            left = { type: 'binaryOp', op, left, right };
        }

        return left;
    }

    private isComparisonOp(type: TokenType): boolean {
        return type === TokenType.EQUALS ||
            type === TokenType.NOT_EQUALS ||
            type === TokenType.LESS_THAN ||
            type === TokenType.LESS_EQUAL ||
            type === TokenType.GREATER_THAN ||
            type === TokenType.GREATER_EQUAL;
    }

    private parseConcat(): ASTNode {
        let left = this.parseAddition();

        while (this.current().type === TokenType.AMPERSAND) {
            this.advance();
            const right = this.parseAddition();
            left = { type: 'binaryOp', op: '&', left, right };
        }

        return left;
    }

    private parseAddition(): ASTNode {
        let left = this.parseMultiplication();

        while (this.current().type === TokenType.PLUS || this.current().type === TokenType.MINUS) {
            const op = this.advance().value;
            const right = this.parseMultiplication();
            left = { type: 'binaryOp', op, left, right };
        }

        return left;
    }

    private parseMultiplication(): ASTNode {
        let left = this.parsePower();

        while (this.current().type === TokenType.MULTIPLY || this.current().type === TokenType.DIVIDE) {
            const op = this.advance().value;
            const right = this.parsePower();
            left = { type: 'binaryOp', op, left, right };
        }

        return left;
    }

    private parsePower(): ASTNode {
        let left = this.parseUnary();

        while (this.current().type === TokenType.POWER) {
            this.advance();
            const right = this.parseUnary();
            left = { type: 'binaryOp', op: '^', left, right };
        }

        return left;
    }

    private parseUnary(): ASTNode {
        if (this.current().type === TokenType.MINUS) {
            this.advance();
            const operand = this.parseUnary();
            return { type: 'unaryOp', op: '-', operand };
        }
        if (this.current().type === TokenType.PLUS) {
            this.advance();
            const operand = this.parseUnary();
            return { type: 'unaryOp', op: '+', operand };
        }
        return this.parsePostfix();
    }

    private parsePostfix(): ASTNode {
        let node = this.parsePrimary();

        while (this.current().type === TokenType.PERCENT) {
            this.advance();
            node = { type: 'percent', operand: node };
        }

        return node;
    }

    private parsePrimary(): ASTNode {
        const token = this.current();

        switch (token.type) {
            case TokenType.NUMBER: {
                this.advance();
                return { type: 'number', value: parseFloat(token.value) };
            }

            case TokenType.STRING: {
                this.advance();
                return { type: 'string', value: token.value };
            }

            case TokenType.BOOLEAN: {
                this.advance();
                return { type: 'boolean', value: token.value === 'true' };
            }

            case TokenType.LPAREN: {
                this.advance();
                const expr = this.parseExpression();
                this.expect(TokenType.RPAREN);
                return expr;
            }

            case TokenType.IDENTIFIER: {
                // Check if this is a function call (IDENTIFIER followed by LPAREN)
                if (this.peek(1).type === TokenType.LPAREN) {
                    return this.parseFunctionCall();
                }
                // Otherwise it's a variable reference
                this.advance();
                return { type: 'identifier', name: token.value };
            }

            default:
                // For unexpected tokens, return 0
                if (token.type === TokenType.EOF) {
                    return { type: 'number', value: 0 };
                }
                this.advance();
                return { type: 'number', value: 0 };
        }
    }

    private parseFunctionCall(): ASTNode {
        const nameToken = this.advance(); // function name
        this.expect(TokenType.LPAREN);

        const args: ASTNode[] = [];

        if (this.current().type !== TokenType.RPAREN) {
            args.push(this.parseExpression());

            while (this.current().type === TokenType.SEMICOLON) {
                this.advance();
                args.push(this.parseExpression());
            }
        }

        this.expect(TokenType.RPAREN);
        return { type: 'functionCall', name: nameToken.value, args };
    }
}
